import * as DateFnsNs from 'date-fns'
import lodash from 'lodash'
import { type MRT_ColumnSizingState } from 'material-react-table'
import ConfirmExportDataLimitPresenter from 'src/main/ConfirmExportDataLimit.presenter'
import { excludedExportDocIds } from 'src/main/Main.scopes'
import { downloadBlobAsFile } from 'src/utils/download-utils'
import { Logger, Presenter } from 'wdc-cube'

import {
    TheConsumerMarketService,
    type ColumnFilter,
    type ColumnSort,
    type EmpresaFilter
} from '../the-consumer-market/tcm_service'

import { type TheConnectionPresenter } from './tc_presenter'
import { TableQSAScope } from './tc_scopes'

type PaginationState = {
    pageIndex: number
    pageSize: number
}

const LOG = Logger.get('TcPresenterForSociosListagem')

const service = TheConsumerMarketService.singleton()

export default class TcPresenterForSociosListagem extends Presenter<TableQSAScope, TheConnectionPresenter> {
    constructor(owner: TheConnectionPresenter) {
        super(owner, owner.scope.listagemTabs.qsaListagem)
        this.__parent = owner
        this.__confirmExportDataPresenter = new ConfirmExportDataLimitPresenter(
            owner.app,
            this.scope.exportDataLimitScope
        )
    }

    private readonly __parent: TheConnectionPresenter
    private readonly __confirmExportDataPresenter: ConfirmExportDataLimitPresenter

    async initialize() {
        this.scope.update = this.update
        this.scope.caption = 'Detalhes dos sócios'
        this.scope.onSelectionChange = this.action(this.__onSocioRowClicked)
        this.scope.onFiltrationChange = this.action(this.__onFiltrationChange)
        this.scope.onPaginationChange = this.action(this.__onPaginationChange)
        this.scope.onSortingChange = this.action(this.__onSortingChange)
        this.scope.onDownloadClick = this.action(this.__onDownloadClicked)
        this.scope.setColumnSizing = this.action(this.__onSetColumnSizing)

        this.scope.exportDataLimitScope.onYesNoClicked = this.action(this.__onYesNoConfirmExport)
        this.scope.exportDataLimitScope.canExcludeAlreadyExported = true

        this.__confirmExportDataPresenter.initialize()

        LOG.debug('ready')
    }

    override release(): void {
        this.__confirmExportDataPresenter.release()
        super.release()
    }

    clear() {
        this.scope.pageIndex = 0
        this.scope.pageSize = 100
        this.scope.dataset = []
        this.scope.datasetVersion++
        this.scope.rowCount = 0
        this.scope.loaded = true
        this.scope.update()
    }

    async fetch(
        filter: EmpresaFilter,
        cfg?: {
            paginationState?: PaginationState
            sorting?: ColumnSort[]
            columnFilters?: ColumnFilter[]
        }
    ) {
        let { paginationState, sorting, columnFilters } = cfg ?? {}
        this.scope.loaded = false
        try {
            if (!columnFilters) {
                columnFilters = this.scope.filters
            }

            if (!paginationState) {
                paginationState = {
                    pageIndex: 0,
                    pageSize: this.scope.pageSize
                }
            }

            if (!sorting) {
                sorting = this.scope.sorting
            }

            let search_after: unknown
            if (paginationState.pageIndex > 0) {
                const lastRow = this.scope.dataset[this.scope.dataset.length - 1]
                if (lastRow) {
                    const sortValues: unknown[] = []
                    for (const sortItem of sorting) {
                        sortValues.push((lastRow as Record<string, unknown>)[sortItem.id])
                    }
                    sortValues.push(lastRow.socio_id)
                    search_after = sortValues
                }
            }

            const resp = await service.fetch({
                listagem_qsa: {
                    sorting,
                    columnFilters,
                    search_after,
                    pageSize: paginationState.pageSize
                },
                filter: filter
            })

            this.scope.pageIndex = paginationState.pageIndex
            this.scope.pageSize = paginationState.pageSize
            this.scope.sorting = sorting

            const dataset = resp.listagem_qsa ?? []
            for (let i = 0; i < dataset.length; i++) {
                const row = dataset[i]
                if (lodash.isString(row.entrada_sociedade_dta)) {
                    row.entrada_sociedade_dta = DateFnsNs.parseISO(row.entrada_sociedade_dta)
                }
            }
            LOG.debug(`qsa_dataset.length=${dataset.length}`)

            this.scope.rowCount = resp.listagem_qsa_agg ? resp.listagem_qsa_agg['qsa-count'].value | 0 : 0
            this.scope.dataset = dataset
            this.scope.datasetVersion++
        } finally {
            this.scope.loaded = true
            this.scope.update()
        }
    }

    private async __onSocioRowClicked(selectionMap: Map<string, boolean>) {
        for (const row of this.scope.dataset) {
            row.selected = selectionMap.get(row.key) === true
        }
        this.scope.update()
    }

    async __onFiltrationChange(columnFilters: ColumnFilter[]) {
        this.scope.filters = columnFilters
        await this.fetch(this.__parent.buildTableFilter(), { columnFilters })
    }

    async __onPaginationChange(pageIndex: number, pageSize: number) {
        if (pageSize !== this.scope.pageSize) {
            pageIndex = 0
        }
        await this.fetch(this.__parent.buildTableFilter(), {
            paginationState: { pageIndex, pageSize }
        })
    }

    async __onSortingChange(sorting: ColumnSort[]) {
        await this.fetch(this.__parent.buildTableFilter(), { sorting })
    }

    async __onSetColumnSizing(columns: MRT_ColumnSizingState) {
        this.scope.columnSizing = columns
    }

    async __onDownloadClicked(columns: string[]) {
        this.scope.exportColumns = columns

        this.scope.exportDataLimitScope.isChecking = true
        this.scope.update()
        try {
            const limitExportData = await service.checkLimitExportData(this.__parent.buildTableFilter(), 'socios')
            await this.scope.exportDataLimitScope.onCheckLimitExportData(limitExportData)
        } finally {
            this.scope.exportDataLimitScope.isChecking = false
        }
    }

    protected async __onYesNoConfirmExport(yes: boolean) {
        LOG.debug(`__onYesNoConfirmExport: yes=${yes}`)
        if (yes) {
            await this.__executeDownload()
        }
        this.scope.exportDataLimitScope.open = false
        this.scope.update()
    }

    async __executeDownload() {
        try {
            this.scope.working = true
            this.scope.exportDataLimitScope.isDownloading = true

            const blob = await service.downloadXlsx(
                this.__parent.buildTableFilter(),
                this.scope.exportColumns,
                'socios',
                this.scope.exportDataLimitScope.qttExportRecords,
                this.__parent.app.compliance,
                excludedExportDocIds(this.scope.exportDataLimitScope)
            )

            downloadBlobAsFile(blob, 'tc-socio.xlsx')
        } finally {
            this.scope.exportDataLimitScope.isDownloading = false
            this.scope.working = false
        }
    }
}
